import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const SIDEBAR_STORAGE_KEY = "gestionalo_portal_sidebar_open";

const MOBILE_BREAKPOINT_PX = 1024;

function getStoredSidebarOpen(): boolean {
  if (typeof window === "undefined") return true;
  if (window.innerWidth < MOBILE_BREAKPOINT_PX) return false;
  try {
    const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    return stored !== "false";
  } catch {
    return true;
  }
}

interface SidebarContextValue {
  isSidebarOpen: boolean;
  /**
   * Whether this shell renders a sidebar AT ALL, as opposed to whether it is
   * currently expanded.
   *
   * PublicWizardLayout provides this context without ever drawing a sidebar —
   * only so the wizard's `useSidebar()` call does not throw — so `isSidebarOpen`
   * alone cannot answer "is there a sidebar to offset a fixed element for".
   *
   * The wizard footer used to infer it from auth instead (`!isAnonymous`), and
   * that proxy broke the moment lazy registration let a visitor authenticate
   * WITHOUT changing shell: RequestsNewWizardRoute locks the shell at first
   * render, deliberately, so registering mid-wizard keeps PublicWizardLayout —
   * and the footer started reserving 16rem for a sidebar that was never drawn.
   * Reported as the footer and the content column falling out of alignment.
   *
   * So the shell declares it, rather than consumers guessing.
   */
  hasSidebar: boolean;
  toggleSidebar: () => void;
  closeSidebar: () => void;
}

const SidebarContext = createContext<SidebarContextValue | undefined>(
  undefined
);

export function SidebarProvider({
  children,
  /**
   * Defaults to true because the ordinary shell (AppLayout) draws one; the
   * sidebar-less wizard shell is the exception and says so explicitly.
   */
  hasSidebar = true,
}: {
  children: ReactNode;
  hasSidebar?: boolean;
}): JSX.Element {
  const [isSidebarOpen, setSidebarOpen] = useState(getStoredSidebarOpen);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(isSidebarOpen));
    } catch {
      // ignore when localStorage is unavailable (e.g. private browsing)
    }
  }, [isSidebarOpen]);

  useEffect(() => {
    const handleResize = (): void => {
      if (window.innerWidth < MOBILE_BREAKPOINT_PX) setSidebarOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const value = useMemo<SidebarContextValue>(
    () => ({
      isSidebarOpen,
      hasSidebar,
      toggleSidebar: () => setSidebarOpen((prev) => !prev),
      closeSidebar: () => setSidebarOpen(false),
    }),
    [isSidebarOpen, hasSidebar]
  );

  return (
    <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
  );
}

export function useSidebar(): SidebarContextValue {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
}
